import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  Text,
  Title,
  Badge,
  Button,
  Progress,
  Avatar,
  Group,
  Stack,
  Paper,
  RingProgress,
  ThemeIcon,
  Transition,
  ScrollArea,
  Table,
  ActionIcon,
  Tooltip,
  Center,
  Box,
  Indicator,
  Loader,
  TextInput,
  Select,
} from '@mantine/core';
import {
  IconPlane,
  IconTrendingUp,
  IconClock,
  IconCheck,
  IconAlertCircle,
  IconChartBar,
  IconSearch,
  IconBuildingWarehouse,
  IconParking,
  IconActivity,
  IconCalendar,
  IconChevronRight,
  IconArrowUp,
  IconArrowDown,
  IconMinus,
} from '@tabler/icons-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import config from '../../config';

// Color schemes
const MANUFACTURER_COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#fa709a', '#fee140'];
const STATUS_COLORS = {
  active: '#38ef7d',
  storage: '#ffa726',
  parked: '#ef5350'
};

const FleetExplorer = () => {
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('size');

  // Fetch operators list
  const {
    data: operators,
    isLoading: loadingOperators,
    error: operatorsError
  } = useAuthenticatedData(`${config.apiBaseUrl}/fleet-explorer/operators`);

  // Fetch selected operator details - only when an operator is selected
  const {
    data: operatorDetail,
    isLoading: loadingDetail,
    error: detailError
  } = useAuthenticatedData(
    selectedOperator ? `${config.apiBaseUrl}/fleet-explorer/operator/${selectedOperator.operatorId}` : null,
    { enabled: !!selectedOperator } // Only enable the query when selectedOperator exists
  );

  // Filter and sort operators
  const filteredOperators = React.useMemo(() => {
    if (!operators) return [];

    let filtered = operators.filter(op =>
      op.operatorName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort operators
    switch (sortBy) {
      case 'size':
        return filtered.sort((a, b) => b.totalFleetSize - a.totalFleetSize);
      case 'age':
        return filtered.sort((a, b) => a.overallAverageAge - b.overallAverageAge);
      case 'name':
        return filtered.sort((a, b) => a.operatorName.localeCompare(b.operatorName));
      default:
        return filtered;
    }
  }, [operators, searchQuery, sortBy]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <IconActivity size={16} />;
      case 'storage': return <IconBuildingWarehouse size={16} />;
      case 'parked': return <IconParking size={16} />;
      default: return <IconPlane size={16} />;
    }
  };

  const getAgeColor = (age) => {
    if (age < 5) return 'green';
    if (age < 10) return 'blue';
    if (age < 15) return 'yellow';
    return 'red';
  };

  const formatManufacturerData = () => {
    if (!operatorDetail?.manufacturerBreakdown) return [];
    return operatorDetail.manufacturerBreakdown.map((m, index) => ({
      name: m.manufacturer,
      value: m.totalAircraft,
      age: m.averageAge,
      color: MANUFACTURER_COLORS[index % MANUFACTURER_COLORS.length]
    }));
  };

  const formatAgeDistribution = () => {
    if (!operatorDetail?.fleetComposition || !Array.isArray(operatorDetail.fleetComposition)) {
      return [];
    }
    const ageGroups = {
      '0-5 years': 0,
      '5-10 years': 0,
      '10-15 years': 0,
      '15-20 years': 0,
      '20+ years': 0
    };

    operatorDetail.fleetComposition.forEach(fleet => {
      const count = fleet.aircraftCount;
      const avgAge = fleet.averageAge;

      if (avgAge < 5) ageGroups['0-5 years'] += count;
      else if (avgAge < 10) ageGroups['5-10 years'] += count;
      else if (avgAge < 15) ageGroups['10-15 years'] += count;
      else if (avgAge < 20) ageGroups['15-20 years'] += count;
      else ageGroups['20+ years'] += count;
    });

    const totalAircraft = operatorDetail.fleetComposition.reduce((sum, f) => sum + f.aircraftCount, 0);

    return Object.entries(ageGroups).map(([range, count]) => ({
      range,
      count,
      percentage: totalAircraft > 0 ? (count / totalAircraft * 100).toFixed(1) : 0
    }));
  };

  // Helper function to safely get total fleet size
  const getTotalFleetSize = () => {
    if (!operatorDetail?.fleetComposition || !Array.isArray(operatorDetail.fleetComposition)) {
      return 0;
    }
    return operatorDetail.fleetComposition.reduce((sum, f) => sum + (f.aircraftCount || 0), 0);
  };

  return (
    <Container size="xl" p="md" mt="lg">
      {/* Header */}
      <Card shadow="lg" radius="lg" p="lg" mb="xl" withBorder>
        <Group justify="space-between">
          <Group>
            <ThemeIcon size="xl" radius="xl" variant="light" color="blue">
              <IconPlane size={28} />
            </ThemeIcon>
            <Stack gap={0}>
              <Title order={2}>Fleet Explorer</Title>
              <Text size="sm" c="dimmed">Fleet intelligence and analytics from Cirium</Text>
            </Stack>
          </Group>
          <Group>
            <Select
              placeholder="Sort by"
              value={sortBy}
              onChange={setSortBy}
              data={[
                { value: 'size', label: 'Fleet Size' },
                { value: 'age', label: 'Average Age' },
                { value: 'name', label: 'Operator Name' }
              ]}
            />
            <Badge size="lg" color="cyan" variant="filled">
              {filteredOperators.length} Operators
            </Badge>
          </Group>
        </Group>
      </Card>

      <Grid>
        {/* Operators List */}
        <Grid.Col span={4}>
          <Card shadow="lg" radius="lg" p="lg" withBorder style={{
            height: 'calc(100vh - 200px)'
          }}>
            <Group justify="space-between" mb="md">
              <Title order={3}>Fleet Operators</Title>
            </Group>

            <TextInput
              placeholder="Search operators..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              mb="md"
              size="sm"
            />

            <ScrollArea style={{ height: 'calc(100% - 60px)' }}>
              <Stack gap="md">
                {loadingOperators ? (
                  <Center p="xl">
                    <Loader color="blue" size="lg" />
                  </Center>
                ) : (
                  filteredOperators.map((operator) => (
                    <Paper
                      key={operator.operatorId}
                      p="md"
                      radius="md"
                      withBorder
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backgroundColor: selectedOperator?.operatorId === operator.operatorId
                          ? 'var(--mantine-color-blue-light)'
                          : undefined
                      }}
                      onClick={() => setSelectedOperator(operator)}
                    >
                      <Group justify="space-between">
                        <Stack gap={5}>
                          <Text size="md" fw={600}>
                            {operator.operatorName}
                          </Text>
                          <Group gap="xs">
                            <Badge size="sm" color="cyan" variant="light">
                              {operator.totalFleetSize} aircraft
                            </Badge>
                            <Badge size="sm" color={getAgeColor(operator.overallAverageAge)} variant="light">
                              Avg {operator.overallAverageAge.toFixed(1)} yrs
                            </Badge>
                          </Group>
                          <Group gap={5}>
                            <Badge size="xs" color="green" leftSection={getStatusIcon('active')}>
                              {operator.totalActive}
                            </Badge>
                            <Badge size="xs" color="orange" leftSection={getStatusIcon('storage')}>
                              {operator.totalStorage}
                            </Badge>
                          </Group>
                        </Stack>
                        <ActionIcon
                          size="sm"
                          radius="xl"
                          variant="filled"
                          color="blue"
                        >
                          <IconChevronRight />
                        </ActionIcon>
                      </Group>
                    </Paper>
                  ))
                )}
              </Stack>
            </ScrollArea>
          </Card>
        </Grid.Col>

        {/* Fleet Details */}
        <Grid.Col span={8}>
          {selectedOperator && loadingDetail ? (
            <Card shadow="lg" radius="lg" p="xl" withBorder style={{
              height: 'calc(100vh - 200px)'
            }}>
              <Center style={{ height: '100%' }}>
                <Stack align="center" gap="xl">
                  <Loader color="blue" size="xl" />
                  <Text size="xl" c="dimmed" ta="center">
                    Loading fleet details...
                  </Text>
                </Stack>
              </Center>
            </Card>
          ) : selectedOperator && detailError ? (
            <Card shadow="lg" radius="lg" p="xl" withBorder bg="red.0" style={{
              height: 'calc(100vh - 200px)'
            }}>
              <Center style={{ height: '100%' }}>
                <Stack align="center" gap="xl">
                  <ThemeIcon size={80} radius="xl" color="red" variant="light">
                    <IconAlertCircle size={40} />
                  </ThemeIcon>
                  <Text size="xl" c="red" ta="center">
                    Error loading fleet details
                  </Text>
                  <Text size="sm" c="dimmed" ta="center">
                    {detailError.message || 'Please try selecting another operator'}
                  </Text>
                </Stack>
              </Center>
            </Card>
          ) : selectedOperator && operatorDetail && operatorDetail.fleetComposition && Array.isArray(operatorDetail.fleetComposition) ? (
            <Stack gap="xl">
              {/* KPI Cards */}
              <Grid>
                <Grid.Col span={3}>
                  <Card shadow="lg" radius="lg" p="md" bg="blue.1" withBorder>
                    <Stack align="center" gap={5}>
                      <ThemeIcon size="xl" radius="xl" variant="light" color="blue">
                        <IconPlane size={24} />
                      </ThemeIcon>
                      <Text size="xs" c="dimmed">Total Fleet</Text>
                      <Text c="dimmed" size="xl" fw={700}>
                        {getTotalFleetSize()}
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Card shadow="lg" radius="lg" p="md" bg="pink.1" withBorder>
                    <Stack align="center" gap={5}>
                      <ThemeIcon size="xl" radius="xl" variant="light" color="pink">
                        <IconCalendar size={24} />
                      </ThemeIcon>
                      <Text size="xs" c="dimmed">Avg Age</Text>
                      <Text c="dimmed" size="xl" fw={700}>
                        {selectedOperator.overallAverageAge?.toFixed(1) || '0.0'} yrs
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Card shadow="lg" radius="lg" p="md" bg="cyan.1" withBorder>
                    <Stack align="center" gap={5}>
                      <ThemeIcon size="xl" radius="xl" variant="light" color="cyan">
                        <IconActivity size={24} />
                      </ThemeIcon>
                      <Text size="xs" c="dimmed">Active</Text>
                      <Text c="dimmed" size="xl" fw={700}>
                        {selectedOperator.totalFleetSize > 0
                          ? ((selectedOperator.totalActive / selectedOperator.totalFleetSize) * 100).toFixed(0)
                          : 0}%
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Card shadow="lg" radius="lg" p="md" bg="violet.1" withBorder>
                    <Stack align="center" gap={5}>
                      <ThemeIcon size="xl" radius="xl" variant="light" color="violet">
                        <IconChartBar size={24} />
                      </ThemeIcon>
                      <Text size="xs" c="dimmed">Types</Text>
                      <Text c="dimmed" size="xl" fw={700}>
                        {operatorDetail.fleetComposition?.length || 0}
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>

              {/* Charts Row */}
              <Grid>
                <Grid.Col span={6}>
                  <Card shadow="lg" radius="lg" p="lg" withBorder>
                    <Title order={4} mb="md">Manufacturer Distribution</Title>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={formatManufacturerData()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: ${entry.value}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {formatManufacturerData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Card shadow="lg" radius="lg" p="lg" withBorder>
                    <Title order={4} mb="md">Age Distribution</Title>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={formatAgeDistribution()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="range" />
                        <YAxis />
                        <RechartsTooltip />
                        <Bar dataKey="count" fill="var(--mantine-color-blue-6)" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Grid.Col>
              </Grid>

              {/* Fleet Composition Table */}
              <Card shadow="lg" radius="lg" p="lg" withBorder>
                <Title order={4} mb="md">Fleet Composition</Title>
                <ScrollArea>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Aircraft Type</Table.Th>
                        <Table.Th>Series</Table.Th>
                        <Table.Th>Manufacturer</Table.Th>
                        <Table.Th>Count</Table.Th>
                        <Table.Th>Avg Age</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>% of Fleet</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {operatorDetail.fleetComposition.map((fleet, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>
                            <Group gap="xs">
                              <ThemeIcon size="sm" radius="xl" color="blue" variant="light">
                                <IconPlane size={14} />
                              </ThemeIcon>
                              <Text fw={500}>{fleet.aircraftType}</Text>
                            </Group>
                          </Table.Td>
                          <Table.Td><Text>{fleet.masterSeries}</Text></Table.Td>
                          <Table.Td><Text>{fleet.manufacturer}</Text></Table.Td>
                          <Table.Td>
                            <Badge color="cyan" variant="light">{fleet.aircraftCount}</Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge color={getAgeColor(fleet.averageAge)} variant="light">
                              {fleet.averageAge?.toFixed(1) || '0.0'} yrs
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Group gap={5}>
                              <Tooltip label="Active">
                                <Badge size="sm" color="green">{fleet.activeCount || 0}</Badge>
                              </Tooltip>
                              <Tooltip label="Storage">
                                <Badge size="sm" color="orange">{fleet.storageCount || 0}</Badge>
                              </Tooltip>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Progress
                              value={fleet.percentOfFleet || 0}
                              color="blue"
                              size="sm"
                              label={`${fleet.percentOfFleet || 0}%`}
                              styles={{
                                label: { fontSize: '10px' }
                              }}
                            />
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Card>
            </Stack>
          ) : (
            <Card shadow="lg" radius="lg" p="xl" withBorder style={{
              height: 'calc(100vh - 200px)'
            }}>
              <Center style={{ height: '100%' }}>
                <Stack align="center" gap="xl">
                  <ThemeIcon size={80} radius="xl" color="blue" variant="light">
                    <IconSearch size={40} />
                  </ThemeIcon>
                  <Text size="xl" c="dimmed" ta="center">
                    Select a fleet operator to view detailed analytics
                  </Text>
                  <Text size="sm" c="dimmed" ta="center">
                    Explore comprehensive fleet composition, age distribution, and operational insights
                  </Text>
                </Stack>
              </Center>
            </Card>
          )}
        </Grid.Col>
      </Grid>
    </Container>
  );
}
export default FleetExplorer;